import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import {
  Search, Tag, Calendar, Eye, ArrowRight, Filter, X, Clock,
  ChevronLeft, ChevronRight, BookOpen, Lock, FileText, ChevronDown
} from 'lucide-react';
import { AuthContext } from '../App';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL;

// Dominios de clientes que NO muestran el blog (dominio propio)
const BLOCKED_BLOG_DOMAINS = [
  'centralsaludfront-production-d227.up.railway.app',
];

export const isBlogAllowed = () => {
  const hostname = window.location.hostname;
  if (hostname === 'centralsalud.es' || hostname === 'www.centralsalud.es') return true;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  if (BLOCKED_BLOG_DOMAINS.includes(hostname)) return false;
  // Por defecto permitido si no tiene configuración especificada
  return true;
};

const Blog = () => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const blogAllowed = isBlogAllowed();

  const [posts, setPosts] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters from URL params
  const page = parseInt(searchParams.get('page')) || 1;
  const categoriaActiva = searchParams.get('categoria') || '';
  const busqueda = searchParams.get('busqueda') || '';
  const etiquetaActiva = searchParams.get('etiqueta') || '';

  const [searchInput, setSearchInput] = useState(busqueda);
  const [showFilters, setShowFilters] = useState(false);

  const updateParam = useCallback((key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // Fetch posts
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', page);
        params.set('limit', '12');
        if (categoriaActiva) params.set('categoria', categoriaActiva);
        if (busqueda) params.set('busqueda', busqueda);
        if (etiquetaActiva) params.set('etiqueta', etiquetaActiva);

        const headers = {};
        const token = localStorage.getItem('token');
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/api/blog/posts?${params.toString()}`, { headers });
        const data = await res.json();
        setPosts(data.posts || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 0);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [page, categoriaActiva, busqueda, etiquetaActiva]);

  // Fetch categories & tags
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [catRes, tagRes] = await Promise.all([
          fetch(`${API_URL}/api/blog/categorias`),
          fetch(`${API_URL}/api/blog/etiquetas`)
        ]);
        const catData = await catRes.json();
        const tagData = await tagRes.json();
        setCategorias(catData.categorias || []);
        setEtiquetas(tagData.etiquetas || []);
      } catch (err) {
        console.error('Error fetching blog metadata:', err);
      }
    };
    fetchMeta();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    updateParam('busqueda', searchInput.trim());
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchInput('');
  };

  const hasFilters = categoriaActiva || busqueda || etiquetaActiva;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const getReadTime = (contenido) => {
    if (!contenido) return '1 min';
    const words = contenido.replace(/<[^>]+>/g, '').split(/\s+/).length;
    return `${Math.max(1, Math.ceil(words / 200))} min`;
  };

  if (!blogAllowed) {
    return <Navigate to="/" />;
  }

  return (
    <div className="blog-page">
      {/* Hero */}
      <div className="blog-hero">
        <div className="blog-hero-content">
          <h1><BookOpen size={36} /> Blog CentralSalud</h1>
          <p>Novedades, anuncios y recursos para la comunidad de CentralSalud</p>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="blog-search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar publicaciones..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit">Buscar</button>
          </form>
        </div>
      </div>

      <div className="blog-container">
        {/* Filters bar */}
        <div className="blog-filters-bar">
          <div className="blog-filters-left">
            <button
              className={`blog-filter-toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={16} />
              Filtros
              <ChevronDown size={14} className={showFilters ? 'rotated' : ''} />
            </button>

            {/* Category pills */}
            <div className="blog-category-pills">
              <button
                className={`blog-pill ${!categoriaActiva ? 'active' : ''}`}
                onClick={() => updateParam('categoria', '')}
              >
                Todos
              </button>
              {categorias.map(cat => (
                <button
                  key={cat.id}
                  className={`blog-pill ${categoriaActiva === cat.slug ? 'active' : ''}`}
                  style={categoriaActiva === cat.slug ? { backgroundColor: cat.color, borderColor: cat.color } : {}}
                  onClick={() => updateParam('categoria', cat.slug)}
                >
                  {cat.nombre}
                  {cat.total_posts > 0 && <span className="pill-count">{cat.total_posts}</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="blog-filters-right">
            <span className="blog-results-count">{total} publicacion{total !== 1 ? 'es' : ''}</span>
            {hasFilters && (
              <button className="blog-clear-filters" onClick={clearFilters}>
                <X size={14} /> Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Tags panel */}
        {showFilters && (
          <div className="blog-tags-panel">
            <h4><Tag size={16} /> Temas populares</h4>
            <div className="blog-tags-list">
              {etiquetas.map(tag => (
                <button
                  key={tag}
                  className={`blog-tag ${etiquetaActiva === tag ? 'active' : ''}`}
                  onClick={() => updateParam('etiqueta', etiquetaActiva === tag ? '' : tag)}
                >
                  #{tag}
                </button>
              ))}
              {etiquetas.length === 0 && <span className="blog-no-tags">No hay etiquetas disponibles</span>}
            </div>
          </div>
        )}

        {/* Active filters */}
        {hasFilters && (
          <div className="blog-active-filters">
            {busqueda && (
              <span className="blog-active-filter">
                Buscando: "{busqueda}"
                <button onClick={() => { updateParam('busqueda', ''); setSearchInput(''); }}><X size={12} /></button>
              </span>
            )}
            {categoriaActiva && (
              <span className="blog-active-filter">
                Categoría: {categorias.find(c => c.slug === categoriaActiva)?.nombre || categoriaActiva}
                <button onClick={() => updateParam('categoria', '')}><X size={12} /></button>
              </span>
            )}
            {etiquetaActiva && (
              <span className="blog-active-filter">
                Etiqueta: #{etiquetaActiva}
                <button onClick={() => updateParam('etiqueta', '')}><X size={12} /></button>
              </span>
            )}
          </div>
        )}

        {/* Posts grid */}
        {loading ? (
          <div className="blog-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="blog-card-skeleton">
                <div className="skeleton-image" />
                <div className="skeleton-content">
                  <div className="skeleton-line short" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line" />
                  <div className="skeleton-line medium" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="blog-empty">
            <BookOpen size={48} />
            <h3>No hay publicaciones</h3>
            <p>{hasFilters ? 'No se encontraron resultados con los filtros aplicados' : 'Aún no hay publicaciones en el blog'}</p>
            {hasFilters && (
              <button onClick={clearFilters} className="blog-btn-secondary">Limpiar filtros</button>
            )}
          </div>
        ) : (
          <>
            <div className="blog-grid">
              {posts.map((post, index) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.id}
                  className={`blog-card ${index === 0 && page === 1 ? 'featured' : ''}`}
                >
                  <div className="blog-card-image">
                    {post.imagen_portada ? (
                      <img src={post.imagen_portada} alt={post.titulo} />
                    ) : (
                      <div className="blog-card-placeholder">
                        <FileText size={32} />
                      </div>
                    )}
                    {post.exclusivo_clientes === 1 && (
                      <div className="blog-card-badge exclusive">
                        <Lock size={12} /> Exclusivo
                      </div>
                    )}
                    {post.categoria_nombre && (
                      <div
                        className="blog-card-badge category"
                        style={{ backgroundColor: post.categoria_color }}
                      >
                        {post.categoria_nombre}
                      </div>
                    )}
                  </div>
                  <div className="blog-card-body">
                    <h3 className="blog-card-title">{post.titulo}</h3>
                    {post.extracto && (
                      <p className="blog-card-excerpt">{post.extracto}</p>
                    )}
                    <div className="blog-card-meta">
                      <span><Calendar size={14} /> {formatDate(post.published_at)}</span>
                      <span><Clock size={14} /> {getReadTime(post.contenido)}</span>
                      <span><Eye size={14} /> {post.vistas}</span>
                    </div>
                    {post.etiquetas && (
                      <div className="blog-card-tags">
                        {post.etiquetas.split(',').slice(0, 3).map(tag => (
                          <span key={tag.trim()} className="blog-card-tag">#{tag.trim()}</span>
                        ))}
                      </div>
                    )}
                    <div className="blog-card-read">
                      Leer más <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="blog-pagination">
                <button
                  disabled={page <= 1}
                  onClick={() => updateParam('page', String(page - 1))}
                >
                  <ChevronLeft size={16} /> Anterior
                </button>

                <div className="blog-pagination-pages">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && <span className="blog-pagination-dots">...</span>}
                        <button
                          className={p === page ? 'active' : ''}
                          onClick={() => updateParam('page', String(p))}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                </div>

                <button
                  disabled={page >= totalPages}
                  onClick={() => updateParam('page', String(page + 1))}
                >
                  Siguiente <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blog;
